<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWavoip } from 'dashboard/composables/useWavoip';

const props = defineProps({
  phoneNumber: {
    type: String,
    default: '',
  },
  contactName: {
    type: String,
    default: '',
  },
  inboxToken: {
    type: String,
    default: null,
  },
  size: {
    type: String,
    default: 'small',
    validator: value => ['small', 'medium'].includes(value),
  },
});

const { t } = useI18n();
const { wavoipStore, makeCall, openWebphone, setDialerInput, isWavoipEnabled } =
  useWavoip();

const isDisabled = computed(() => {
  return (
    !isWavoipEnabled.value ||
    !wavoipStore.isInitialized ||
    wavoipStore.isCallActive ||
    wavoipStore.isCallOutgoing ||
    !props.phoneNumber
  );
});

const tooltipText = computed(() => {
  if (!isWavoipEnabled.value) return t('WAVOIP.DISABLED');
  if (!wavoipStore.isInitialized) return t('WAVOIP.NOT_INITIALIZED');
  if (wavoipStore.isCallActive || wavoipStore.isCallOutgoing)
    return t('WAVOIP.CALL_IN_PROGRESS');
  if (!props.phoneNumber) return t('WAVOIP.NO_PHONE_NUMBER');
  return t('WAVOIP.CALL_VIA_WHATSAPP', {
    name: props.contactName || props.phoneNumber,
  });
});

const handleClick = async () => {
  if (isDisabled.value) return;

  // Set the phone number in the webphone dialer
  setDialerInput(props.phoneNumber);

  // Open the webphone widget
  openWebphone();

  // Start the call
  await makeCall(props.phoneNumber, props.inboxToken);
};
</script>

<template>
  <button
    v-if="isWavoipEnabled"
    v-tooltip="tooltipText"
    class="wavoip-dialer-btn"
    :class="[
      `wavoip-dialer-btn--${size}`,
      { 'wavoip-dialer-btn--disabled': isDisabled },
    ]"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <fluent-icon icon="call" :size="size === 'small' ? 16 : 20" />
  </button>
</template>

<style scoped>
.wavoip-dialer-btn {
  @apply flex items-center justify-center rounded-lg border-0
    cursor-pointer transition-all duration-200
    bg-green-500 text-white hover:bg-green-600
    dark:bg-green-600 dark:hover:bg-green-700;
}

.wavoip-dialer-btn--small {
  @apply h-8 w-8;
}

.wavoip-dialer-btn--medium {
  @apply h-10 w-10;
}

.wavoip-dialer-btn--disabled {
  @apply opacity-40 cursor-not-allowed hover:bg-green-500
    dark:hover:bg-green-600;
}
</style>
